"use client"

import * as React from "react"
import { Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "#core/cn"

function TooltipProvider({
  delayDuration = 600,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

// A closing menu gives focus back to its trigger, and the browser can mark that focus
// :focus-visible after a click or an Escape. Only a Tab moves focus to a control on purpose.
let lastKeyWasTab = false
if (typeof document !== "undefined") {
  document.addEventListener("keydown", (event) => (lastKeyWasTab = event.key === "Tab"), true)
  document.addEventListener("pointerdown", () => (lastKeyWasTab = false), true)
}

const OPEN_MENU = [
  "dropdown-menu-content",
  "dropdown-menu-sub-content",
  "context-menu-content",
  "context-menu-sub-content",
  "popover-content",
  "select-content",
]
  .map((slot) => `[data-slot=${slot}][data-state=open]`)
  .join(", ")

// An open menu owns the pointer. Even a hint it hides arms the next hint to open with no delay.
function outsideOpenMenu(element: Element) {
  const menus = [...document.querySelectorAll(OPEN_MENU)]
  return menus.length > 0 && !menus.some((menu) => menu.contains(element))
}

function TooltipTrigger({
  onFocus,
  onPointerMove,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onPointerMove={(event) => {
        onPointerMove?.(event)
        if (outsideOpenMenu(event.currentTarget)) event.preventDefault()
      }}
      onFocus={(event) => {
        onFocus?.(event)
        // A focus from the pointer or from a closing overlay must not open it, nothing would close it.
        if (!lastKeyWasTab) event.preventDefault()
      }}
      {...props}
    />
  )
}

function TooltipContent({
  className,
  sideOffset = 8,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "z-50 inline-flex w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) items-center gap-1.5 rounded-lg bg-foreground px-3 py-1.5 text-xs text-background has-data-[slot=kbd]:pr-1.5 **:data-[slot=kbd]:relative **:data-[slot=kbd]:isolate **:data-[slot=kbd]:z-50 **:data-[slot=kbd]:rounded-sm",
          className
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
